import algoliasearch from 'algoliasearch/lite';
import { sponsoredProducts as SponsoredProductsUtil } from 'utils';
import tracer from './tracer';
import nodeRequester from './algoliaAxiosRequester';

/**
 * Enrich the algolia results with a custom ranking algorithm to get relevant search results
 * ex : when a user searches for "robinet", they are actually looking for "mitigeur", so we push relevant products
 * at the top of the result page
 *
 * JIRA : REC-1820
 */
const rerankRelevantProducts = tracer.wrap(
    'fragmentListing.rerankRelevantProducts',
    (response, ruleContexts = []) => {
        const shouldRerankProducts =
            ruleContexts.includes('reranking-search-legacy') ||
            ruleContexts.includes('reranking-search-legacy-alternate');
        if (shouldRerankProducts) {
            const position = ruleContexts.includes('reranking-search-legacy')
                ? 'reranking_positions'
                : 'reranking_positions.alternate';
            const newResponse = { ...response };
            const { hits } = newResponse.results[0];
            const query = newResponse.results[0].query.toLowerCase();
            const [rerankedHits, noReranking] = hits.reduce(
                ([pass, fail], elem) => {
                    return elem[position] && elem[position][query]
                        ? [[...pass, { ...elem, reranked: true }], fail]
                        : [pass, [...fail, elem]];
                },
                [[], []],
            );
            rerankedHits.sort(
                (a, b) => a[position][query] - b[position][query],
            );
            newResponse.results[0].hits = [...rerankedHits, ...noReranking];
            newResponse.results[0].rerankedCount = rerankedHits.length || 0;
            return newResponse;
        }
        return response;
    },
);

export const rerankProducts = ({ response, rerankingConfig }) => {
    const {
        categoryId,
        listingType,
        sponsoredProductsAds = [],
        urlModelId,
        hasPromotedFilter = false,
    } = rerankingConfig;

    const { hits } = response.results[0];
    const { PRODUCTS_CARDS_POSITIONS } = SponsoredProductsUtil;
    const RECOMMENDED_POSITION = 3;
    const productPositions = PRODUCTS_CARDS_POSITIONS.concat();
    const productsToRerank = [];

    if (listingType === 'search') {
        // We need to decrease all positions by 1 because we don't have any promoted card
        productPositions.forEach((pos, i) => productPositions[i] - 1);
        productPositions.unshift(RECOMMENDED_POSITION);
        productPositions.pop();
    } else if (listingType === 'category') {
        // Extract specific category cards (topSales, recommended, google shopping )
        const topSaleProductIndex = hits.findIndex(
            product =>
                product.banner.categories &&
                product.banner.categories.includes(`topSales.${categoryId}`),
        );
        if (topSaleProductIndex > -1) {
            productsToRerank.push({
                position: 0,
                product: hits.splice(topSaleProductIndex, 1)[0],
            });
        }

        // gs stands for Google Shopping
        const gsProductIndex = hits.findIndex(
            product => product.model_id === parseInt(urlModelId, 10),
        );
        if (gsProductIndex > -1) {
            productsToRerank.push({
                position: 0,
                product: hits.splice(gsProductIndex, 1)[0],
            });
        }

        const recommendedProductIndex = hits.findIndex(
            product =>
                product.banner.categories &&
                (product.banner.categories.includes(
                    `recommended.${categoryId}`,
                ) ||
                    product.banner.categories.includes(
                        `productOfTheWeek.${categoryId}`,
                    )),
        );
        // No recommended product ? Yeah, we take the place !
        if (recommendedProductIndex === -1) {
            productPositions.unshift(
                hasPromotedFilter
                    ? RECOMMENDED_POSITION
                    : // We need to offset the recommended position
                      // as the topSale will take a full row
                      RECOMMENDED_POSITION + 1,
            );
            productPositions.pop();
        } else {
            productsToRerank.push({
                position: hasPromotedFilter
                    ? RECOMMENDED_POSITION
                    : RECOMMENDED_POSITION + 1,
                product: hits.splice(recommendedProductIndex, 1)[0],
            });
        }
    }
    // Extract sponsored products and their corresponding position
    sponsoredProductsAds.forEach(({ product_id: productId }) => {
        const sponsoredProductIndex = hits.findIndex(
            // objectID is a string in Algolia ¯\_(ツ)_/¯
            ({ objectID }) => `${productId}` === objectID,
        );
        if (sponsoredProductIndex > -1) {
            const position = productPositions.shift();
            productsToRerank.push({
                position,
                product: hits.splice(sponsoredProductIndex, 1)[0],
            });
        }
    });

    productsToRerank.forEach(({ position, product }) =>
        hits.splice(position, 0, product),
    );
    return response;
};

export default tracer.wrap(
    'fragmentListing.createAlgoliaApp',
    function createAlgoliaApp({
        appId,
        searchApiKey,
        isSSR,
        rerankingConfig = {},
    }) {
        const options = {};
        if (isSSR) {
            options.headers = {
                'Accept-Encoding': 'gzip',
                'User-Agent': 'ms-ssr-listing',
                Host: `${appId}-dsn.algolia.net`,
            };
            options.hosts = [
                {
                    url:
                        process.env.ENVOY_PROXY_HOST ||
                        `${appId}-dsn.algolia.net`,
                    accept: 1,
                    protocol: process.env.ENVOY_PROXY_SCHEME || 'http',
                },
            ];
            options.requester = nodeRequester();
        }

        const algoliaSearchClient = algoliasearch(appId, searchApiKey, options);

        const search = algoliaSearchClient.search.bind(algoliaSearchClient);
        algoliaSearchClient.search = (...params) =>
            search(...params)
                .then(response =>
                    rerankRelevantProducts(
                        response,
                        params[0]?.[0]?.params?.ruleContexts,
                    ),
                )
                .then(response =>
                    rerankProducts({
                        response,
                        rerankingConfig,
                    }),
                );

        return algoliaSearchClient;
    },
);
